const STORAGE_PREFIX = 'promptBuffer';
const STORAGE_VERSION = 'v1';
const STORAGE_NAMESPACE = `${STORAGE_PREFIX}:${STORAGE_VERSION}`;
const EXPIRY_DAYS = 7;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
const MAX_REFINE_ENTRIES = 20;

export const PROMPT_BUFFER_SCOPES = {
  GLOBAL: 'global',
  REFINE: 'refine',
};

let cachedStorage = null;
let storageChecked = false;

function getStorage() {
  if (cachedStorage || storageChecked) {
    return cachedStorage;
  }

  storageChecked = true;

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const testKey = '__prompt_buffer_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    cachedStorage = window.localStorage;
    return cachedStorage;
  } catch (error) {
    // fall back to sessionStorage
  }

  try {
    const testKey = '__prompt_buffer_test__';
    window.sessionStorage.setItem(testKey, '1');
    window.sessionStorage.removeItem(testKey);
    cachedStorage = window.sessionStorage;
    return cachedStorage;
  } catch (error) {
    // storage not available
  }

  cachedStorage = null;
  return cachedStorage;
}

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
}

function buildKey({ scope, strategyId = null, revisionId = null }) {
  const parts = [STORAGE_NAMESPACE, scope];

  if (scope === PROMPT_BUFFER_SCOPES.REFINE && strategyId) {
    parts.push(strategyId);
    if (revisionId) {
      parts.push(revisionId);
    }
  }

  return parts.join(':');
}

function isExpired(entry) {
  if (!entry || !entry.expiresAt) return false;
  return Date.now() > entry.expiresAt;
}

function pruneEntries(storage) {
  if (!storage) return;

  const refineEntries = [];

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (!key || !key.startsWith(STORAGE_NAMESPACE)) continue;

    const raw = storage.getItem(key);
    const entry = safeParse(raw);

    if (!entry || entry.version !== STORAGE_VERSION) {
      storage.removeItem(key);
      continue;
    }

    if (isExpired(entry) || !entry.value || !entry.value.trim()) {
      storage.removeItem(key);
      continue;
    }

    if (entry.scope === PROMPT_BUFFER_SCOPES.REFINE) {
      refineEntries.push({ key, entry });
    }
  }

  if (refineEntries.length > MAX_REFINE_ENTRIES) {
    refineEntries
      .sort((a, b) => (b.entry.lastUpdated || 0) - (a.entry.lastUpdated || 0))
      .slice(MAX_REFINE_ENTRIES)
      .forEach(({ key }) => storage.removeItem(key));
  }
}

export function loadPromptBuffer(config) {
  const storage = getStorage();
  if (!storage) return null;

  pruneEntries(storage);

  const key = buildKey(config);
  const raw = storage.getItem(key);
  if (!raw) return null;

  const entry = safeParse(raw);
  if (!entry || entry.version !== STORAGE_VERSION) {
    storage.removeItem(key);
    return null;
  }

  if (isExpired(entry)) {
    storage.removeItem(key);
    return null;
  }

  return {
    value: entry.value || '',
    attachmentsMeta: Array.isArray(entry.attachmentsMeta) ? entry.attachmentsMeta : [],
    lastUpdated: entry.lastUpdated || 0,
    expiresAt: entry.expiresAt || 0,
    scope: entry.scope,
    strategyId: entry.strategyId || null,
    revisionId: entry.revisionId || null,
  };
}

export function savePromptBuffer(config, { value, attachmentsMeta = [] }) {
  const storage = getStorage();
  if (!storage) return;

  const trimmed = (value || '').trim();
  const key = buildKey(config);

  if (!trimmed) {
    clearPromptBuffer(config);
    return;
  }

  const entry = {
    version: STORAGE_VERSION,
    scope: config.scope,
    strategyId: config.strategyId || null,
    revisionId: config.revisionId || null,
    value: trimmed,
    attachmentsMeta: Array.isArray(attachmentsMeta) ? attachmentsMeta : [],
    lastUpdated: Date.now(),
    expiresAt: Date.now() + EXPIRY_MS,
  };

  try {
    storage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    // If quota exceeded, attempt to prune and retry once
    try {
      pruneEntries(storage);
      storage.setItem(key, JSON.stringify(entry));
    } catch (innerError) {
      throw innerError || error;
    }
  }

  pruneEntries(storage);
}

export function clearPromptBuffer(config) {
  const storage = getStorage();
  if (!storage) return;

  const key = buildKey(config);
  storage.removeItem(key);
}

export function clearAllPromptBuffers() {
  const storage = getStorage();
  if (!storage) return;

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key && key.startsWith(STORAGE_NAMESPACE)) {
      storage.removeItem(key);
    }
  }
}

export const PROMPT_BUFFER_CONSTANTS = {
  STORAGE_NAMESPACE,
  STORAGE_VERSION,
  EXPIRY_MS,
  MAX_REFINE_ENTRIES,
};
